/**
 * Modular Figure Class
 * Handles composition of body parts and kinematic chains.
 */
window.Figure = class Figure {
  /**
   * @param {p5.Vector} startingPos 
   * @param {number} scale - Global scale factor
   * @param {Object} config - Optional overrides
   */
  constructor(startingPos, scale = 1.0, config = {}) {
    this.pos = startingPos.copy();
    this.scale = scale;
    
    // Proportional configuration based on scale
    const bodyWidth = 90 * scale;
    this.config = {
      hipOffset: { x: 0, y: -50 * scale },
      headOffset: { x: 0, y: -270 * scale },
      bodySize: { w: bodyWidth * scale, h: 180 * scale },
      leg: {
        thigh: { length: 100 * scale, width: bodyWidth/2 * scale },
        shin: { length: 90 * scale, width: bodyWidth/2 * scale },
        foot: { length: 40 * scale, width: bodyWidth/2 * scale }
      },
      arm: {
        upper: { length: 80 * scale },
        forearm: { length: 70 * scale }
      },
      ...config
    };

    this.hipPos = createVector();
    this.shoulderL = createVector();
    this.shoulderR = createVector();
    this.prevAnklePos = createVector();
    this.angles = { foot: 0 }; 

    // Initialize Kinematic Chains
    this.legChain = new KinematicChain(
      this.hipPos, 
      this.config.leg.thigh.length, 
      this.config.leg.shin.length
    );

    this.armChainL = new KinematicChain(
      this.shoulderL,
      this.config.arm.upper.length,
      this.config.arm.forearm.length
    );

    this.armChainR = new KinematicChain(
      this.shoulderR,
      this.config.arm.upper.length,
      this.config.arm.forearm.length
    );

    this._updatePositions();
  }

  /**
   * Update the figure's state based on interaction
   */
  update() {
    this.prevAnklePos.set(this.anklePos);

    // Re-sync hipPos every frame so all parts stay relative to this.pos
    // (needed when pos changes externally, e.g. on window resize)
    this.hipPos.set(this.pos)
      .add(this.config.hipOffset.x, this.config.hipOffset.y)
      .add(this.config.leg.thigh.width / 2, 0);

    interaction.update(this.hipPos);
    this.legChain.solve(interaction.ikTarget);
    this.angles.foot = this.legChain.angles.a2;

    // Update Shoulders
    const torsoTopY = this.hipPos.y - this.config.bodySize.h;
    this.shoulderL.set(this.pos.x - this.config.bodySize.w / 2 - 20 * this.scale, torsoTopY + 20 * this.scale);
    this.shoulderR.set(this.pos.x + this.config.bodySize.w / 2 + 20 * this.scale, torsoTopY + 20 * this.scale);

    // Static Arm Targets (Relative to shoulders)
    const armTargetL = createVector(
      this.shoulderL.x - 50 * this.scale, 
      this.shoulderL.y + 140 * this.scale
    );
    const armTargetR = createVector(
      this.shoulderR.x + 50 * this.scale, 
      this.shoulderR.y + 140 * this.scale
    );

    this.armChainL.solve(armTargetL);
    this.armChainR.solve(armTargetR, true); // Reverse the angle for the right arm
  }

  /**
   * Draw the figure parts
   */
  draw() {
    // Draw Head
    fill(0); 
    noStroke();
    const headRadius = this.config.leg.thigh.width * 2;
    circle(this.pos.x, this.pos.y + this.config.headOffset.y, headRadius);
    
    // Draw Body
    rectMode(CENTER);
    fill(0);
    // Body is placed above the hips
    const bodyY = this.hipPos.y - this.config.bodySize.h / 2;
    rect(this.pos.x, bodyY, this.config.bodySize.w, this.config.bodySize.h);
    
    // Draw Leg Segments
    noFill();
    stroke(0);
    strokeWeight(this.config.leg.thigh.width);
    strokeJoin(ROUND);
    strokeCap(ROUND);
    
    const ankle = this.legChain.joints.p2;
    const knee = this.legChain.joints.p1;
    const hip = this.hipPos;

    // Calculate toe position
    const footLen = this.config.leg.foot.length - (10 * this.scale);
    const toeX = ankle.x + cos(this.angles.foot) * footLen;
    const toeY = ankle.y + sin(this.angles.foot) * footLen;

    beginShape();
    // Static other leg (for visual balance)
    const staticFootY = hip.y + this.config.leg.thigh.length + this.config.leg.shin.length + footLen;
    vertex(hip.x - this.config.leg.thigh.width, staticFootY);
    vertex(hip.x - this.config.leg.thigh.width, hip.y);
    
    // The animated leg
    vertex(hip.x, hip.y);
    vertex(knee.x, knee.y);
    vertex(ankle.x, ankle.y);
    vertex(toeX, toeY);
    endShape();

    // Draw Arms (as defined by image: outlined segments)
    this._drawArm(this.armChainL);
    this._drawArm(this.armChainR);
  }

  /**
   * Internal helper to draw an outlined arm
   */
  _drawArm(chain) {
    const p0 = chain.origin;
    const p1 = chain.joints.p1;
    const p2 = chain.joints.p2;
    const w = this.config.leg.thigh.width;

    // Draw Outer (Black)
    stroke(0);
    strokeWeight(w);
    noFill();
    beginShape();
    vertex(p0.x, p0.y);
    vertex(p1.x, p1.y);
    vertex(p2.x, p2.y);
    endShape();

    // Draw Inner (White)
    stroke(255);
    strokeWeight(w - (4 * this.scale));
    beginShape();
    vertex(p0.x, p0.y);
    vertex(p1.x, p1.y);
    vertex(p2.x, p2.y);
    endShape();
  }

  /**
   * Helper to get current ankle position
   */
  get anklePos() {
    return this.legChain.joints.p2;
  }

  _updatePositions() {
    this.hipPos.set(this.pos)
      .add(this.config.hipOffset.x, this.config.hipOffset.y)
      .add(this.config.leg.thigh.width / 2, 0);

    // Define where the leg should "rest" when not being dragged
    const restX = this.hipPos.x;
    const restY = this.hipPos.y + this.config.leg.thigh.length + this.config.leg.shin.length;
    interaction.setRestPosition(restX, restY);
    
    // Update interaction limits based on scale
    interaction.dragLimits = { 
        minX: -80 * this.scale, 
        maxX: 80 * this.scale, 
        minY: 10 * this.scale, 
        maxY: 180 * this.scale 
    };
  }
}

